import { useSyncExternalStore } from "react";
import type { Role } from "./types";

/**
 * Who is signed in to the back office: a store admin (the same account as the website admin),
 * with a 12-hour token. Never a till PIN. Pages see them as a manager, which is what the API
 * gives a back-office sign-in.
 */
export type OfficeRole = "owner" | "gm" | "manager" | "accountant" | "stock";
export const OFFICE_ROLE_LABEL: Record<OfficeRole, string> = { owner: "Owner", gm: "General manager", manager: "Manager", accountant: "Accountant", stock: "Stock keeper" };
export type OfficeUser = { id: string; name: string; email: string; role?: OfficeRole };
type State = { token: string | null; user: OfficeUser | null };

const KEY = "office.session";
let state: State = load();
const subs = new Set<() => void>();

function load(): State {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return { token: null, user: null, ...JSON.parse(raw) };
  } catch {
    /* private mode */
  }
  return { token: null, user: null };
}

function save(next: State) {
  state = next;
  try {
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
  subs.forEach((f) => f());
}

export const session = {
  get: () => state,
  signIn: (token: string, user: OfficeUser) => save({ token, user }),
  signOut: () => save({ token: null, user: null }),
  subscribe: (f: () => void) => {
    subs.add(f);
    return () => subs.delete(f);
  },
};

const view = () => ({ ...state, staff: state.user ? { id: state.user.id, name: state.user.name, role: "manager" as Role, color: "#017880" } : null });
let cached = view();
session.subscribe(() => {
  cached = view();
});

/** The signed-in admin, also as `staff` (a manager) for pages that check a role. */
export function useSession() {
  return useSyncExternalStore(session.subscribe, () => cached, () => cached);
}
